import { useEffect, useMemo } from 'react';
import omit from 'lodash-es/omit';
import { useIntakeFormShallowStore, useIntakeFormStore } from '@frontend/api-intake-form';
import { OnboardingCoreHooks } from '@frontend/onboarding-core';
import { ONBOARDING_DASHBOARD_PATH, useMultiOnboardingDashboardConfig } from '@frontend/onboarding-dashboard';
import { AnySearch, useMatchRoute, useNavigate, useSearch, type AnyRoute } from '@frontend/weave-router';
import { INTAKE_FORM_PATHS, INTAKE_FORM_PATH_V2, NWX_DASHBOARD_PATH } from '../constants';

// Custom Hook used to determine whether a redirect to the onboarding page should occur for the current location.
export const useOnboardingRedirect = () => {
  const urlSearchParam = useSearch({
    from: '/home/dashboard',
    shouldThrow: false,
  });
  const matchRoute = useMatchRoute();
  const { setIntakeFormLocationListInfo, setLocationIdForQuery } = useIntakeFormShallowStore(
    'setIntakeFormLocationListInfo',
    'setLocationIdForQuery'
  );

  const {
    locationIdForQuery,
    intakeFormLocationsConfig,
    isLoading: isLoadingOnboardingInfo,
    isLoadingFastPassEligibility,
    fastPassIntakeFormLocationsConfig,
    pendingFastPassIntakeFormLocationsConfig,
    fastPassLocationIds,
    isFetchedLocationHierarchyConfig,
  } = OnboardingCoreHooks.useGetLocationHierarchyConfig({
    showErrorAlert: true,
    prepareIntakeFormConfig: true,
  });

  // TODO: Deprecate intake form v1 route
  const isIntakeFormV1Route = !!matchRoute({ to: INTAKE_FORM_PATHS.root as AnyRoute, fuzzy: true });
  const isIntakeFormV2Route = !!matchRoute({ to: INTAKE_FORM_PATH_V2 as AnyRoute });
  const isHomeDashboardPage = !!matchRoute({ to: NWX_DASHBOARD_PATH });
  const isOnboardingDashboardPage = !!matchRoute({ to: ONBOARDING_DASHBOARD_PATH });
  const navigate = useNavigate();

  const shouldCheckForOnboardingRedirect = isHomeDashboardPage && urlSearchParam?.checkOnboarding === true;

  const isFastPassEligible = fastPassIntakeFormLocationsConfig && fastPassIntakeFormLocationsConfig.length > 0;
  const isFastPassIntakeFormEligible =
    pendingFastPassIntakeFormLocationsConfig && pendingFastPassIntakeFormLocationsConfig.length > 0;

  useEffect(() => {
    useIntakeFormStore.reset();
    // This location id is used in intake form to clear query cache on form completion
    setLocationIdForQuery(locationIdForQuery);
  }, [locationIdForQuery, setLocationIdForQuery]);

  useEffect(() => {
    if (isFastPassIntakeFormEligible) {
      // If fast-pass eligible, only set locations eligible for fast-pass in intake form store
      setIntakeFormLocationListInfo({
        intakeFormLocations: pendingFastPassIntakeFormLocationsConfig,
        ownIntakeFormLocations: pendingFastPassIntakeFormLocationsConfig,
        isShowIntakeForm: true,
      });
    } else if (intakeFormLocationsConfig) {
      setIntakeFormLocationListInfo(intakeFormLocationsConfig);
    }
  }, [
    isFastPassIntakeFormEligible,
    intakeFormLocationsConfig,
    pendingFastPassIntakeFormLocationsConfig,
    setIntakeFormLocationListInfo,
  ]);

  const shouldShowIntakeForm = intakeFormLocationsConfig?.isShowIntakeForm;

  const {
    isLoading: isLoadingOBDRedirection,
    isOnboardingDashboardRedirectionEligible,
    showOnboardingDashboard,
  } = useMultiOnboardingDashboardConfig({
    locationIds: fastPassLocationIds,
    fetchOnboardingLocationsConfig: false,
  });

  const onboardingRedirectUrl = useMemo(() => {
    if (!isFetchedLocationHierarchyConfig || isLoadingOnboardingInfo || isLoadingFastPassEligibility) {
      return '';
    }
    const isOnIntakeFormRoute = isFastPassIntakeFormEligible ? isIntakeFormV2Route : isIntakeFormV1Route;
    const intakeFormPath = isFastPassIntakeFormEligible ? INTAKE_FORM_PATH_V2 : INTAKE_FORM_PATHS.root;

    // Redirect to intake form
    if (shouldShowIntakeForm && !isOnIntakeFormRoute) {
      return intakeFormPath;
    }

    // Wait for onboarding dashboard redirection eligibility to load
    if (isLoadingOBDRedirection) {
      return '';
    }
    // Redirect to Dashboard if on intake form route
    if (!shouldShowIntakeForm && (isIntakeFormV1Route || isIntakeFormV2Route)) {
      return isOnboardingDashboardRedirectionEligible ? ONBOARDING_DASHBOARD_PATH : NWX_DASHBOARD_PATH;
    }
    // Redirect to onboarding dashboard if eligible
    if (shouldCheckForOnboardingRedirect && isOnboardingDashboardRedirectionEligible) {
      return ONBOARDING_DASHBOARD_PATH;
    }
    // Redirect to dashboard if onboarding dashboard is not eligible
    if (isOnboardingDashboardPage && !showOnboardingDashboard) {
      return NWX_DASHBOARD_PATH;
    }
    return '';
  }, [
    isFetchedLocationHierarchyConfig,
    isLoadingOnboardingInfo,
    isLoadingFastPassEligibility,
    isFastPassIntakeFormEligible,
    isIntakeFormV2Route,
    isIntakeFormV1Route,
    shouldShowIntakeForm,
    isLoadingOBDRedirection,
    shouldCheckForOnboardingRedirect,
    isOnboardingDashboardRedirectionEligible,
    isOnboardingDashboardPage,
    showOnboardingDashboard,
  ]);

  const routerSearchParams = useSearch({ strict: false });
  // Remove `checkOnboarding` query param if onboarding dashboard redirection is not needed
  useEffect(() => {
    if (
      !isFetchedLocationHierarchyConfig ||
      isLoadingOnboardingInfo ||
      isLoadingFastPassEligibility ||
      !shouldCheckForOnboardingRedirect ||
      isLoadingOBDRedirection
    ) {
      return;
    }
    if (!isFastPassEligible || !isOnboardingDashboardRedirectionEligible) {
      navigate({
        to: NWX_DASHBOARD_PATH,
        search: omit(routerSearchParams, ['checkOnboarding']) as AnySearch,
        replace: true,
      });
    }
  }, [
    isLoadingOnboardingInfo,
    isLoadingFastPassEligibility,
    isFetchedLocationHierarchyConfig,
    isFastPassEligible,
    isLoadingOBDRedirection,
    isOnboardingDashboardRedirectionEligible,
    navigate,
    routerSearchParams,
    shouldCheckForOnboardingRedirect,
  ]);

  return {
    shouldShowOnboardingLoader: isLoadingOnboardingInfo,
    onboardingRedirectUrl,
  };
};
